import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "../lib/api";
import { connectYoutubePopup } from "../lib/youtubeOauth";
import { btnPrimary } from "../components/ui-bits";
import VoicePickerButton from "../components/VoicePickerButton";
import { toast } from "sonner";
import { ArrowLeft, Youtube, Loader2, Users, Eye, Film, Sparkles, Globe2, Settings2, Mic2, Palette, Unplug, CheckCircle2 } from "lucide-react";

// Transiciones desactivadas (2026-07): el backend fuerza corte directo en el render pase lo
// que pase lo que se guarde aquí (ver TabComposicion.jsx), así que no se ofrece selector.
const TRANSITIONS_UI_ENABLED = false;
const TRANSFORM_IDS = ["Espejo", "Color correction", "Crop 97%", "Film grain", "Retro"];
const THEME_IDS = ["default", "dark", "color", "pill", "rule"];

const inputCls = "w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-lg bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/20 outline-none";
const sectionHeadCls = "flex items-center gap-2 text-lg font-bold text-slate-900 mb-4";

function buildForm(channel) {
  return {
    name: channel.name || "",
    description: channel.description || "",
    language: channel.language || "es",
    default_voice: channel.default_voice || "",
    default_transitions: channel.default_transitions || ["Corte directo"],
    default_subtitles: channel.default_subtitles !== undefined ? channel.default_subtitles : true,
    default_transforms: channel.default_transforms || [],
    default_theme: channel.default_theme || "default",
    youtube_url: channel.youtube_url || "",
    reference_channels: (channel.reference_channels || []).join("\n"),
  };
}

export default function ChannelConfigPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [section, setSection] = useState("general");
  const [yt, setYt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ytOauth, setYtOauth] = useState(null); // null=cargando | {connected, channel_title}
  const [oauthConnecting, setOauthConnecting] = useState(false);

  const loadOauthStatus = () => {
    api.get("/youtube/status", { params: { channel_id: id } })
      .then((r) => setYtOauth(r.data)).catch(() => setYtOauth({ connected: false }));
  };

  useEffect(() => {
    api.get(`/channels/${id}`).then((r) => {
      setChannel(r.data);
      const f = buildForm(r.data);
      setForm(f);
      setInitial(f);
      if (r.data.youtube_url) loadYt();
    }).catch((e) => { toast.error(apiErr(e)); navigate("/app"); }).finally(() => setLoading(false));
    loadOauthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const connectOauth = async () => {
    setOauthConnecting(true);
    try {
      await connectYoutubePopup(id);
      toast.success(t("chm.oauthConnected"));
      loadOauthStatus();
      loadYt();
    } catch (e) {
      if (e.message !== "popup_closed") toast.error(apiErr(e));
    } finally { setOauthConnecting(false); }
  };
  const disconnectOauth = async () => {
    if (!window.confirm(t("chm.oauthDisconnectConfirm"))) return;
    try {
      await api.post("/youtube/disconnect", null, { params: { channel_id: id } });
      setYtOauth({ connected: false });
      toast.success(t("chm.oauthDisconnected"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Se llama automáticamente al cargar la página (si el canal ya tiene youtube_url, derivada al
  // conectar por OAuth) -- no la dispara el usuario, así que si falla no interrumpe con un toast,
  // simplemente no se muestra la tarjeta de estadísticas.
  const loadYt = async () => {
    try {
      const { data } = await api.get(`/channels/${id}/youtube`);
      setYt(data.connected ? data : null);
    } catch (e) { setYt(null); }
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleTransform = (tid) => setForm((f) => ({ ...f, default_transforms: f.default_transforms.includes(tid) ? f.default_transforms.filter((x) => x !== tid) : [...f.default_transforms, tid] }));

  const dirty = useMemo(() => form && initial && JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  const save = async () => {
    if (!form.name.trim()) { toast.warning(t("chm.warnName")); setSection("general"); return; }
    if (!form.description.trim()) { toast.warning(t("chm.warnDesc")); setSection("general"); return; }
    setBusy(true);
    const payload = {
      name: form.name.trim(), description: form.description, language: form.language,
      default_voice: form.default_voice, default_transitions: form.default_transitions,
      default_subtitles: form.default_subtitles, default_transforms: form.default_transforms,
      default_theme: form.default_theme, youtube_url: form.youtube_url.trim(),
      reference_channels: form.reference_channels.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try {
      const { data } = await api.put(`/channels/${id}`, payload);
      toast.success(t("chm.updated"));
      setChannel(data);
      const f = buildForm(data);
      setForm(f);
      setInitial(f);
      queryClient.invalidateQueries({ queryKey: ["channels"] });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const SECTIONS = [
    { id: "general", label: t("chm.navGeneral"), icon: Globe2 },
    { id: "youtube", label: t("chm.youtubeLabel"), icon: Youtube },
    { id: "referencias", label: t("chm.refLabel"), icon: Sparkles },
    { id: "narracion", label: t("chm.navVoice"), icon: Mic2 },
    { id: "estilo", label: t("chm.navStyle"), icon: Palette },
  ];

  if (loading || !form) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={40} /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/app")} className="flex items-center gap-3 group shrink-0" data-testid="channel-config-brand">
            <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-9 w-auto group-hover:opacity-80 transition-opacity" />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <button data-testid="channel-config-back" onClick={() => navigate(`/app/channels/${id}`)} className="inline-flex items-center gap-2 px-3 py-2.5 text-slate-500 hover:text-slate-800 font-semibold">
              <ArrowLeft size={18} /> {t("chm.back")}
            </button>
            <button data-testid="save-channel-btn" onClick={save} disabled={busy || !dirty} className={`${btnPrimary} px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed`}>
              {busy ? t("chm.saving") : t("chm.saveChanges")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">{t("chm.configChannel")}{channel?.name ? `: ${channel.name}` : ""}</h1>

        <div className="flex flex-col md:flex-row gap-6">
          <nav className="hidden md:flex md:w-52 md:flex-shrink-0 md:flex-col md:gap-1">
            {SECTIONS.map((s) => {
              const active = section === s.id;
              const Icon = s.icon;
              return (
                <button key={s.id} data-testid={`config-nav-${s.id}`} onClick={() => setSection(s.id)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-left font-semibold text-base transition-colors ${active ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon size={18} className="shrink-0" /> {s.label}
                </button>
              );
            })}
          </nav>
          <nav className="flex md:hidden gap-1 overflow-x-auto overflow-y-hidden vb-scroll pb-1">
            {SECTIONS.map((s) => {
              const active = section === s.id;
              const Icon = s.icon;
              return (
                <button key={s.id} data-testid={`config-nav-mobile-${s.id}`} onClick={() => setSection(s.id)}
                  className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap font-semibold text-base border-b-[3px] transition-colors ${active ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                  <Icon size={16} className="shrink-0" /> <span className={active ? "inline" : "hidden sm:inline"}>{s.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
            {section === "general" && (
              <div className="space-y-5">
                <p className={sectionHeadCls}><Globe2 size={18} /> {t("chm.navGeneral")}</p>
                <div>
                  <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.nameLabel")}</label>
                  <input data-testid="channel-name-input" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder={t("chm.namePlaceholder")} className={inputCls} />
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">{t("chm.nameYtHint")}</p>
                </div>
                <div>
                  <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.descLabel")}</label>
                  <textarea data-testid="channel-desc-input" value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2} placeholder={t("chm.descPlaceholder")} className={inputCls} />
                  <p className="text-sm text-slate-500 mt-2">{t("chm.descHint")}</p>
                </div>
                <div>
                  <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.languageLabel")}</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="channel-language">
                    {[{ id: "es", label: "Español", flag: "https://flagcdn.com/es.svg" }, { id: "en", label: "English", flag: "https://flagcdn.com/gb.svg" }].map((l) => (
                      <button key={l.id} type="button" data-testid={`channel-lang-${l.id}`} onClick={() => set({ language: l.id, default_voice: "" })}
                        className={`flex items-center justify-center gap-2 border-2 rounded-lg px-3 py-2.5 text-base font-medium transition-colors bg-white ${form.language === l.id ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                        <img src={l.flag} alt="" className="w-6 h-auto rounded-sm shadow-sm" /> {l.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">{t("chm.languageHint")}</p>
                </div>
              </div>
            )}

            {section === "youtube" && (
              <div className="space-y-4">
                <p className={sectionHeadCls}><Youtube size={18} className="text-red-600" /> {t("chm.youtubeLabel")}</p>

                {ytOauth?.connected ? (
                  <div data-testid="channel-oauth-connected" className="flex items-center justify-between gap-3 border-2 border-emerald-200 bg-emerald-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-base font-bold text-emerald-900">{t("chm.oauthConnected")}</p>
                        {ytOauth.channel_title && <p className="text-sm text-emerald-700 truncate">{t("chm.oauthConnectedAs", { name: ytOauth.channel_title })}</p>}
                      </div>
                    </div>
                    <button type="button" data-testid="channel-oauth-disconnect" onClick={disconnectOauth}
                      className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-red-600">
                      <Unplug size={15} /> {t("chm.oauthDisconnect")}
                    </button>
                  </div>
                ) : (
                  <button type="button" data-testid="channel-oauth-connect" onClick={connectOauth} disabled={oauthConnecting || ytOauth === null}
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-60">
                    {oauthConnecting ? <Loader2 className="animate-spin" size={20} /> : <Youtube size={20} />} {t("chm.oauthConnect")}
                  </button>
                )}

                {yt && (
                  <div data-testid="channel-youtube-info" className="flex items-center gap-4 border-2 border-slate-200 rounded-xl p-3 bg-slate-50">
                    {yt.thumbnail && <img src={yt.thumbnail} alt={yt.title} className="w-14 h-14 rounded-full object-cover" />}
                    <div className="min-w-0">
                      <p className="text-base font-bold text-slate-900 truncate">{yt.title}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600 mt-0.5">
                        {yt.subscribers != null && <span className="flex items-center gap-1"><Users size={14} /> {yt.subscribers.toLocaleString()} {t("chm.subs")}</span>}
                        <span className="flex items-center gap-1"><Eye size={14} /> {yt.views.toLocaleString()} {t("chm.views")}</span>
                        <span className="flex items-center gap-1"><Film size={14} /> {yt.videos.toLocaleString()} {t("chm.videos")}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {section === "referencias" && (
              <div className="space-y-4">
                <p className={sectionHeadCls}><Sparkles size={18} className="text-amber-500" /> {t("chm.refLabel")} <span className="text-slate-400 font-normal text-sm">{t("chm.refNote")}</span></p>
                <textarea data-testid="channel-reference-channels" value={form.reference_channels} onChange={(e) => set({ reference_channels: e.target.value })} rows={5} placeholder={"https://youtube.com/@CanalCompetencia1\nhttps://youtube.com/@CanalCompetencia2"} className={inputCls} />
                <p className="text-sm text-slate-500">{t("chm.refHint")}</p>
              </div>
            )}

            {section === "narracion" && (
              <div className="space-y-4">
                <p className={sectionHeadCls}><Mic2 size={18} /> {t("chm.navVoice")}</p>
                <div>
                  <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.voiceLabel")}</label>
                  <VoicePickerButton language={form.language} value={form.default_voice} onChange={(v) => set({ default_voice: v.id })} />
                  <button type="button" data-testid="channel-voice-later" onClick={() => set({ default_voice: "" })}
                    className="block mt-2 text-sm text-slate-500 hover:text-slate-700 font-semibold underline underline-offset-2">
                    {t("chm.chooseAtVideoCreate")}
                  </button>
                </div>
                <label className="flex items-center gap-3 border-2 border-slate-200 rounded-lg px-4 py-3 cursor-pointer bg-white">
                  <input type="checkbox" data-testid="channel-subtitles" checked={form.default_subtitles} onChange={(e) => set({ default_subtitles: e.target.checked })} className="w-6 h-6 accent-blue-600 shrink-0" />
                  <span className="text-base font-semibold text-slate-800">{t("chm.subtitlesDefault")}</span>
                </label>
              </div>
            )}

            {section === "estilo" && (
              <div className="space-y-5">
                <p className={sectionHeadCls}><Palette size={18} /> {t("chm.navStyle")}</p>
                {TRANSITIONS_UI_ENABLED && (
                  <div>
                    <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.transitionsLabel")}</label>
                    <div className="grid grid-cols-2 gap-2 max-h-[160px] overflow-y-auto vb-scroll" data-testid="channel-transitions">
                      {(form.default_transitions || []).map((tr) => (
                        <span key={tr}>{tr}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-base font-semibold text-slate-800 mb-2">{t("chm.themeLabel")}</label>
                  <div className="space-y-2" data-testid="channel-theme">
                    {THEME_IDS.map((tid) => (
                      <label key={tid} className={`flex items-start gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors bg-white ${form.default_theme === tid ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                        <input type="radio" data-testid={`channel-theme-${tid}`} name="channel-theme" checked={form.default_theme === tid} onChange={() => set({ default_theme: tid })} className="w-5 h-5 accent-blue-600 shrink-0 mt-0.5" />
                        <img src={`/theme-previews/${tid}.jpg`} alt="" className="w-28 sm:w-32 aspect-video rounded-md object-cover shrink-0 border border-slate-200" />
                        <span>
                          <span className="block text-base font-medium text-slate-800">{t(`chm.theme.${tid}.name`)}</span>
                          <span className="block text-sm text-slate-500 mt-0.5">{t(`chm.theme.${tid}.desc`)}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-base font-semibold text-slate-800 mb-2">{t("comp.transformsTitle")}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="channel-transforms">
                    {TRANSFORM_IDS.map((tid) => (
                      <label key={tid} className={`flex items-center gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors bg-white ${form.default_transforms.includes(tid) ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                        <input type="checkbox" data-testid={`channel-transform-${tid}`} checked={form.default_transforms.includes(tid)} onChange={() => toggleTransform(tid)} className="w-5 h-5 accent-blue-600 shrink-0" />
                        <span className="text-base font-medium text-slate-800">{t(`comp.transform.${tid}.name`)}</span>
                        <span className="text-sm text-slate-500 ml-auto">{t(`comp.transform.${tid}.desc`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
